<template>
  <nav v-if="totalPages" class="nav-pagination front g-24">
    <div>
      <div class="counts row full-width">
        <b>{{ displayedItems > totalItems ? totalItems : displayedItems }}</b>
        {{ $t('pagination.from') }}
        <b>{{ totalItems }}</b>
        <span style="text-transform: lowercase">{{ entity }}</span>
      </div>
      <div class="progress">
        <div class="fill" :style="{width: progressWidth + '%'}"></div>
      </div>
    </div>
   
    <template v-if="loadMore && currentPage < totalPages">
      <Button ref="loadMoreButton" :always-show-spinner="true" :loading="loading" @click="loadMorePages"
              v-if="$device.isMobile"
              type="black"
              class="tall wide uppercase" :text="$t('pagination.load_more',{count:itemsPerPage,type:entity})"/>
      <Button :loading="loading" @click="loadMorePages" :always-show-spinner="true" v-else
              type="black"
              class="tall wide uppercase" :text="$t('pagination.load_more',{count:itemsPerPage,type:entity})"/>
    </template>

    <div class="nav-pagination-container row">
      <!-- Previous Page Link -->
      <div class="item page-item item-left" v-if="currentPage > 1">
        <nuxt-link :to="prevPageUrl" rel="prev" @click.prevent="$emit('change', currentPage-1)">
          <ExpandIcon style="transform: rotate(180deg); margin-top: 7px;"/>
        </nuxt-link>
      </div>

      <!-- Pagination Elements -->
      <div class="pagination g-16 row">
        <nuxt-link v-for="n in paginationRange"
                   :key="n"
                   :class="['page-item', { 'active': n === currentPage }]" @click.prevent="$emit('change', n)"
                   :to="generatePageUrl(n)">{{
            n
          }}
        </nuxt-link>
      </div>

      <!-- Next Page Link -->
      <div class="page-item item-right" v-if="currentPage < totalPages">
        <nuxt-link :to="nextPageUrl" rel="next" @click.prevent="$emit('change', currentPage+1)">
          <ExpandIcon style="margin-top: 7px;"/>
        </nuxt-link>
      </div>
    </div>
  </nav>
</template>

<script>
import Button from "~/components/atoms/Button.vue";
import LeftIcon from "~/components/icons/LeftIcon.vue";
import RightIcon from "~/components/icons/RightIcon.vue";
import ExpandIcon from "~/components/icons/ExpandIcon.vue";

export default {
  components: {ExpandIcon, RightIcon, LeftIcon, Button},
  props: {
    currentPage: Number,
    totalPages: Number,
    totalItems: Number,
    count: Number,
    itemsPerPage: Number,
    hasMorePages: Boolean,
    entity: String,
    moreButton: Boolean,
    moreQty: Number,
    loadMore: Boolean,
    loading: Boolean,
    url: String,
  },
  data() {
    const currentPath = this.$route.path;
    return {
      currentPath,
      // Mobile: first load requires a click; after that auto-pull via IntersectionObserver.
      autoLoadEnabled: false,
    }
  },
  mounted() {
    window.addEventListener('urlReplaced', this.handlePopState);
  },
  beforeUnmount() {
    window.removeEventListener('urlReplaced', this.handlePopState);
    this.disconnectObserver();
  },
  watch: {
    loading(isLoading, wasLoading) {
      if (wasLoading && !isLoading && this.autoLoadEnabled) {
        this.$nextTick(() => this.initIntersectionObserver());
      }
    },
  },
  computed: {
    getWindowPath() {
      return this.currentPath
    },
    displayedItems() {
      const calculated = (this.itemsPerPage * (this.currentPage - 1)) + this.count;
      if (this.loadMore && (this.count > this.itemsPerPage || calculated > this.totalItems)) {
        return this.count;
      }
      return calculated;
    },
    progressWidth() {
      const width = this.totalItems > 0 ? (this.displayedItems / this.totalItems) * 100 : 0;
      return width > 100 ? 100 : width;
    },
    entityName() {
      const names = {product: 'product', collection: 'collection', blog: 'blog'};
      return names[this.entity] || 'item';
    },
    prevPageUrl() {
      return this.currentPage > 1 ? this.generatePageUrl(this.currentPage - 1) : '#';
    },
    nextPageUrl() {
      return this.currentPage < this.totalPages ? this.generatePageUrl(this.currentPage + 1) : '#';
    },
    paginationRange() {
      const rangeStart = Math.max(1, this.currentPage - 2);
      const rangeEnd = Math.min(rangeStart + 4, this.totalPages);
      return Array.from({length: rangeEnd - rangeStart + 1}, (item, index) => rangeStart + index);
    }
  },
  methods: {
    handlePopState() {
      this.currentPath = window.location.pathname;
    },
    generatePageUrl(page) {
      const currentQuery = {...(this.$route?.query || {})};

      if (page === 1) {
        delete currentQuery.page;
      } else {
        currentQuery.page = String(page);
      }

      const queryString = new URLSearchParams(currentQuery).toString();
      return queryString ? `${this.currentPath}?${queryString}` : this.currentPath;
    },
    loadMorePages() {
      if (this.loading || !this.totalPages || this.currentPage >= this.totalPages) {
        return
      }

      // First mobile tap unlocks infinite scroll for the rest of this pagination session.
      if (this.$device.isMobile && !this.autoLoadEnabled) {
        this.autoLoadEnabled = true;
      }

      this.$emit('more', this.currentPage + 1);
    },
    disconnectObserver() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
    },
    initIntersectionObserver() {
      if (!this.$device.isMobile || !this.autoLoadEnabled || !this.$refs.loadMoreButton) {
        return;
      }

      this.disconnectObserver();

      this.observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !this.loading && this.currentPage < this.totalPages) {
            this.loadMorePages();
          }
        });
      }, {
        root: null,
        rootMargin: '0px',
        threshold: 0.1,
      });

      this.observer.observe(this.$refs.loadMoreButton.$el);
    }
  }
}
</script>

<style scoped lang="scss">
.nav-pagination {
  .counts {
    margin-top: 15px;
    color: $black;
    justify-content: center;
    gap: 5px;

    b {
      font-weight: 600;
    }
  }

  &.front {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-top: 30px;
    margin-bottom: 20px;

    .nav-pagination-container {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      gap: 5px;
    }

    .pagination {
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 0;
    }

    .progress {
      width: 280px;
      min-height: 2px;
      margin-top: 8px;
      background: $border-grey;
      position: relative;

      .fill {
        max-width: 100%;
        position: absolute;
        left: 0;
        height: 2px;
        background: $dark-black;
      }
    }

    .page-item {
      margin: 0 5px;
    }

    .page-item, .page-link {
      width: 40px;
      height: 40px;
      min-width: 40px;
      min-height: 40px;
      max-width: 40px;
      max-height: 40px;
      align-items: center;
      display: flex;
      justify-content: center;
      color: $black;
      font-size: 16px;
      font-weight: 500;
      cursor: pointer;
      margin: 0;
      border-radius: 100%;

      &:hover {
        background: $light-grey;
      }

      &.active {
        background: $dark-black;
        color: $white;
        width: 40px;
        height: 40px;

        span {
          color: $white;
        }

        &:hover {
          a, span {
            background: $dark-black;
            color: $white;
          }
        }
      }

      &.disabled {
        a, span {
          cursor: default;
        }

        &:hover {
          background: unset;

          a, span {
            background: unset;
            color: unset;
          }
        }
      }
    }
  }
}
</style>
